// src/features/devices/thunks/moveDeviceOnDragEnd.ts
import { createAsyncThunk } from "@reduxjs/toolkit";
import { AppDispatch, IAppThunk, IRootState } from "../../app/store";
import { selectActivePartialMcbimSpace } from "../../McBim/mcbimSelectors";
import {
  addNonPublischedDevicesFromAlgosInRedux,
  getHeightFromFirstCeilingGrid,
  setDeviceOnTheMoveInRedux,
  setSelectedSpaceNumberInRedux,
  thunkToast,
} from "../designSlice";
import { selectDeviceById, selectHttpDevices } from "./deviceSelectors";
import {
  getCenterOfBoundingBox,
  getRandomCoordinateInPolygon,
  PartialMcBimSpace,
} from "../../utils";
import { IBoundingBox, IDeviceType, IRoomSummary } from "@backend/src/shared/schemas/schemas";
import {
  adjustObject,
  calculateTileSizeFromBoundingBox,
  copyDevicesFromASpaceToAnotherSpace,
} from "@multiconsult-group/multiplacer-core";

import { openapi } from "../../../openapi";
import { selectCompCodePath } from "../../globalSelectors";
import {
  calculateTileBoundingBoxFromCoordinateAndDirection,
  convertAVectorTo60cm,
  convertMcBimItemToDevice,
  getPointASubPointB,
  IPointAPointB,
  moveBoundingBox,
  withWorldZInCoordinate,
} from "./deviceUtils";
import { convertMlResponseToDevice } from "../designThunk";
import { selectDevicesExistInRoom } from "./deviceSlice";
import { selectHttpGetDeviceTypes } from "../deviceTypesSelectors";
import { DEVICE_DISCIPLINE_MAPPING } from "@multiconsult-group/multiplacer-core";
import { selectCachedMcbimBuildingStoriesForCurrentProject } from "../designSelector";
import {
  captureBeforeInRedux,
  clearBefore,
  pushCommandInRedux,
  selectPendingBeforeForDevice,
} from "../history/deviceHistorySlice";
import { IDevice } from "@multiconsult-group/multiplacer-core";
import { selectHttpSingleRoomFull } from "../../Room/httpRoomSelectors";
import { selectHttpMcbimSpacesResult } from "../../McBim/httpMcBimSelectors";
import { setSectionCenterCoordinateInRedux } from "../../ViewerRedux/viewerSlice";

const selectProjectNo = (state: IRootState) => state.design.projectNo;
// 🔎 Selektorer — bytt ut til dine faktiske selektorer
const selectSnapToGrid = (s: IRootState) => s.design.snapToGrid;

type Payload = { id: string; points: number[][] };

const getCenterOfPolygonBounds = (points: number[][]): [number, number] => {
  if (points.length === 0) {
    throw new Error("Polygon must contain at least one point");
  }

  const xs = points.map((point) => point[0]);
  const ys = points.map((point) => point[1]);

  return [
    (Math.min(...xs) + Math.max(...xs)) / 2,
    (Math.min(...ys) + Math.max(...ys)) / 2,
  ];
};

export const thunkMoveDeviceOnDragEnd = createAsyncThunk<
  void,
  Payload,
  { state: IRootState; dispatch: AppDispatch }
>("devices/moveOnDragEnd", async ({ id, points }, { getState, dispatch, rejectWithValue }) => {
  const state = getState();

  // Rensa drag-state direkt
  dispatch(setDeviceOnTheMoveInRedux(undefined));

  if (id === "room-section-center-handle") {
    dispatch(setSectionCenterCoordinateInRedux(getCenterOfPolygonBounds(points)));
    return;
  }

  const devices = selectHttpDevices(state);
  const mcbimSpaces = selectHttpMcbimSpacesResult(state);
  const snapToGrid = selectSnapToGrid(state);
  const deviceTypes = selectHttpGetDeviceTypes(state);

  const device = devices.find((d) => d._id === id);
  if (!device) {
    // Inga alerts i thunk – kasta kontrollerat fel
    return rejectWithValue("Device not found");
  }

  const centerOfBoundingBox = getCenterOfBoundingBox(points);
  const floorElevationM = state.design.floorAndJob?.elevation;
  const activeRoom = selectHttpSingleRoomFull(state);
  const mcbimSpace = mcbimSpaces.find((s: any) => s.name === device.roomNumber);

  // Bygg upp nytt device-objekt
  const updated = {
    room: activeRoom,
    mcbimSpace,
    device: {
      ...device,
      coordinate: withWorldZInCoordinate(
        centerOfBoundingBox,
        floorElevationM,
        device.bottomEdgeHeightAboveFloorMm
      ),
      boundingBox: points as IBoundingBox,
      updatedAt: new Date().toISOString(),
    },
  };

  //TODO: Nya ceilinggrids. Är en array av grids, varje grid har lines: number[][]. Refaktorera nedan
  const ceilingGrids = mcbimSpace?.ceilingGrids;
  const deviceType = deviceTypes.find((t) => t.compCode === device.compCode);
  const tileSize =
    device.tileSize ??
    (deviceType?.boundingBox != null
      ? calculateTileSizeFromBoundingBox(deviceType.boundingBox as number[][])
      : undefined);

  const hasCeiling = ceilingGrids != null && ceilingGrids.length > 0;

  // Snap til takrutenett om tilgjengelig
  if (activeRoom && hasCeiling && mcbimSpace?.polygon && snapToGrid) {
    try {
      /*const grid = calculateRoomGridDepricated(
        getRoomPicDepricated(mcbimSpace.polygon, undefined, activeRoom.ceilingCoordinate)
      );
      console.log("Starting adjusting grid");
      const adjusted = adjustObject(
        { boundingBox: updated.device.boundingBox, direction: updated.device.direction },
        grid
      );*/

      if (ceilingGrids) {
        const adjusted = adjustObject(
          {
            center: [updated.device.coordinate[0], updated.device.coordinate[1]],
            direction: updated.device.direction,
            tileSize,
          },
          ceilingGrids
        );
        if (adjusted) {
          updated.device.coordinate = withWorldZInCoordinate(
            adjusted.center,
            floorElevationM,
            updated.device.bottomEdgeHeightAboveFloorMm
          );
          if (adjusted.direction) updated.device.direction = adjusted.direction;
        }
      }
    } catch (e) {
      // håll tyst i UI men logga i konsolen
      console.warn("Error adjusting device position", e);
    }
  }

  if (tileSize) updated.device.tileSize = tileSize;

  const deviceId = String(updated.device._id);

  if (!state.deviceHistory.isApplyingHistory) {
    const before = selectPendingBeforeForDevice(deviceId)(state);

    if (before) {
      const changed =
        before.coordinate[0] !== updated.device.coordinate[0] ||
        before.coordinate[1] !== updated.device.coordinate[1] ||
        JSON.stringify(before.boundingBox) !== JSON.stringify(updated.device.boundingBox) ||
        JSON.stringify(before.direction) !== JSON.stringify(updated.device.direction);

      if (changed) {
        dispatch(
          pushCommandInRedux({
            type: "UPDATE_DEVICE",
            deviceId,
            before,
            after: updated.device,
            meta: {
              createdAtIso: new Date().toISOString(),
              projectNo: updated.device.projectNo,
              roomNumber: updated.device.roomNumber,
              reason: "MOVE",
            },
          })
        );
      }

      dispatch(clearBefore({ deviceId }));
    }
  }

  if (updated.device._id == null) {
    return;
  }

  // RTK Query-anrop via dispatch
  await dispatch(
    openapi.endpoints.putMultiplacerDeviceUpdate.initiate({
      body: { device: { ...updated.device, _id: String(updated.device._id) } },
    })
  ).unwrap();
});

export const thunkStartMove =
  (id: string): IAppThunk =>
  (dispatch, getState) => {
    const state = getState();
    const device = selectDeviceById(state, id);

    if (device) {
      dispatch(captureBeforeInRedux({ device }));
    }
    if (device?.roomNumber != null) {
      dispatch(setSelectedSpaceNumberInRedux(device.roomNumber));
    }
    dispatch(setDeviceOnTheMoveInRedux(id));
  };

export const copyDevicesFromStandardRoomToSelectedRoomThunk = (): IAppThunk => {
  return async (dispatch, getState) => {
    const state = getState();
    const compCodePath = selectCompCodePath(state);

    if (!compCodePath) {
      console.log("No compCodePath found in state");
      return;
    }
    const selectedSpaceNumber = state.design.selectedSpaceNumber;

    if (!selectedSpaceNumber) {
      console.log("No selected space number");
      return;
    }

    const selectedRoom = selectHttpSingleRoomFull(state);

    if (!selectedRoom) {
      console.log("Selected room not found in rooms");
      return;
    }

    if (!selectedRoom.rfpMalRom) {
      console.log("Selected room has no standard room defined");
      return;
    }

    const mcbimSpace = selectActivePartialMcbimSpace(state);
    if (!mcbimSpace) {
      console.log("No active McBim space found");
      return;
    }

    const devicesExistsInRoom = selectDevicesExistInRoom(state);
    if (devicesExistsInRoom) {
      dispatch(
        thunkToast({
          text: `Målrommet ${selectedRoom.rfpMalRom} inneholder allerede enheter. Kopiering av enheter fra standardrom er derfor ikke mulig.`,
          header: "Advarsel",
          isSuccess: false,
        })
      );
      console.log("Devices already exist in target room");
      return;
    }

    try {
      // 1) Hämta items i standardSpace via RTK Query
      const mcbimItemsInStandardSpace = await dispatch(
        openapi.endpoints.getMcbimItemsInSpace.initiate({
          projectNo: state.design.projectNo,
          spaceName: selectedRoom.rfpMalRom,
        })
      ).unwrap(); // <- här får du loss datan (t.ex. McBimItem[])

      const standardSpace = await dispatch(
        openapi.endpoints.getMcbimSingleMcbimSpace.initiate({
          projectNo: state.design.projectNo,
          name: selectedRoom.rfpMalRom,
        })
      ).unwrap();

      if (!standardSpace) {
        dispatch(
          thunkToast({
            text: `Standardrommet ${selectedRoom.rfpMalRom} ble ikke funnet.`,
            header: "Advarsel",
            isSuccess: false,
          })
        );
        console.log("Standard space not found");
        return;
      }

      if (mcbimItemsInStandardSpace.length === 0) {
        dispatch(
          thunkToast({
            text: `Ingen enheter ble funnet i standardrommet ${selectedRoom.rfpMalRom}.`,
            header: "Advarsel",
            isSuccess: false,
          })
        );
        console.log("No items found in standard room");
        return;
      }

      const ceilingHeight = getHeightFromFirstCeilingGrid(standardSpace.ceilingGrids ?? []);

      const devicesToCopyFromStandardSpace = mcbimItemsInStandardSpace.reduce((acc, mcbimItem) => {
        const device = convertMcBimItemToDevice({
          mcBimItem: mcbimItem,
          roomNumber: selectedSpaceNumber,
          roomName: mcbimSpace.longName,
          compCodePath: compCodePath,
          projectNo: getState().design.projectNo,
          ceilingHeight: ceilingHeight ?? 2700,
        });
        if (device != null) {
          device.roomNumber = selectedSpaceNumber;
          device.floor = mcbimSpace.buildingStory?.name ?? "unknown";
          acc.push(device);
        }
        return acc;
      }, [] as IDevice[]);

      console.log("Standard room items", devicesToCopyFromStandardSpace);

      const copiedDevices = copyDevicesFromASpaceToAnotherSpace({
        devicesToCopy: devicesToCopyFromStandardSpace,
        spaceToCopyFrom: standardSpace as PartialMcBimSpace,
        spaceToCopyTo: mcbimSpace as PartialMcBimSpace,
        roomToCopyTo: selectedRoom as IRoomSummary,
      });
      if (copiedDevices.length === 0) {
        dispatch(
          thunkToast({
            text: `Ingen enheter kunne kopieres fordi målrommet skiller seg for mye fra kildrommet ${standardSpace?.name}.`,
            header: "Advarsel",
            isSuccess: false,
          })
        );
        return;
      }

      const activeFloorElevationM = state.design.floorAndJob?.elevation;
      const buildingStories = selectCachedMcbimBuildingStoriesForCurrentProject(state);
      const buildingStory = buildingStories.find(
        (story) => story.name === mcbimSpace.buildingStory?.name
      );
      const bottomEdgeHeightAboveFloorMm =
        getHeightFromFirstCeilingGrid(mcbimSpace.ceilingGrids ?? []) ?? 2700;
      const floorElevationM = buildingStory?.elevation ?? activeFloorElevationM;

      const enrichedCopiedDevices = copiedDevices.map((device) => ({
        ...device,
        floor: mcbimSpace.buildingStory?.name ?? device.floor,
        bottomEdgeHeightAboveFloorMm,
        coordinate: withWorldZInCoordinate(
          device.coordinate,
          floorElevationM,
          bottomEdgeHeightAboveFloorMm
        ),
      }));

      dispatch(addNonPublischedDevicesFromAlgosInRedux(enrichedCopiedDevices));
    } catch (error) {
      console.error("Failed to load standard room items", error);
    }
  };
};

export const thunkAddMlDevicesToSelectedRoom = (): IAppThunk => {
  return async (dispatch, getState) => {
    const state = getState();
    const projectNo = selectProjectNo(state);
    const floorAndJob = getState().design.floorAndJob;

    if (floorAndJob?.floorName == null) {
      console.log("floorName is null");
      return;
    }

    const selectedRoom = selectHttpSingleRoomFull(state);

    if (!selectedRoom) {
      console.log("Selected room not found");
      return;
    }

    const mlPredictedDevices = selectedRoom.mlPredictedDevices;
    if (!mlPredictedDevices || mlPredictedDevices.length === 0) {
      dispatch(
        thunkToast({
          text: `Det er ingen AI-predikterte enheter for rommet ${selectedRoom.roomNumber}.`,
          header: "Info",
          isSuccess: true,
        })
      );
      return;
    }

    const converted = mlPredictedDevices.map((mlDevice) =>
      convertMlResponseToDevice(mlDevice, {
        projectNo: String(projectNo),
        floor: floorAndJob.floorName,
        roomName: selectedRoom.roomName,
        roomNumber: selectedRoom.roomNumber,
        description: "",
        compCode: "",
      })
    );

    dispatch(addNonPublischedDevicesFromAlgosInRedux(converted));
  };
};

export const addDeviceTypeToSelectedRoomThunk = (deviceType: IDeviceType): IAppThunk => {
  return async (dispatch, getState) => {
    const state = getState();
    const selectedSpaceNumber = state.design.selectedSpaceNumber;
    const projectNo = selectProjectNo(state);
    const compCodePath = selectCompCodePath(state);

    if (!selectedSpaceNumber) {
      console.log("No selected space number");
      return;
    }
    if (!compCodePath) {
      console.log("No compCodePath found in state");
      return;
    }
    const room = selectHttpSingleRoomFull(state);
    if (!room) {
      console.log("No active room found");
      return;
    }

    const mcbimSpace = selectActivePartialMcbimSpace(state);
    if (!mcbimSpace?.polygon) {
      console.log("No active McBim space found");
      return;
    }

    // --- NEW: ceiling height (mm) from space.name === roomNumber ---
    const spaces = selectHttpMcbimSpacesResult(state);
    const spaceForRoom = spaces.find((s: any) => s?.name === room.roomNumber);
    const bottomEdgeHeightAboveFloorMm = getHeightFromFirstCeilingGrid(
      spaceForRoom?.ceilingGrids ?? []
    );
    const floorElevationM = state.design.floorAndJob?.elevation;
    // -------------------------------------------------------------

    const firstLine = mcbimSpace.polygon[0];
    const secondLine = mcbimSpace.polygon[1];

    const pointAPointB: IPointAPointB = {
      pointA: { x: firstLine[0], y: firstLine[1] },
      pointB: { x: secondLine[0], y: secondLine[1] },
    };

    const _coord = getRandomCoordinateInPolygon(mcbimSpace.polygon as number[][]);

    const boundingBox = moveBoundingBox(
      calculateTileBoundingBoxFromCoordinateAndDirection(pointAPointB),
      _coord as { x: number; y: number }
    );

    const tileSize =
      deviceType.boundingBox != null
        ? calculateTileSizeFromBoundingBox(deviceType.boundingBox as number[][])
        : undefined;

    const device: IDevice = {
      description: deviceType.typeName ?? "",
      deviceCategory: deviceType.deviceCategory,
      discipline: DEVICE_DISCIPLINE_MAPPING[deviceType.deviceCategory],
      compCode: deviceType.compCode as string,
      boundingBox,
      coordinate: withWorldZInCoordinate(
        getCenterOfBoundingBox(boundingBox),
        floorElevationM,
        bottomEdgeHeightAboveFloorMm ?? 2700
      ),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      direction: convertAVectorTo60cm(getPointASubPointB(pointAPointB)),
      bottomEdgeHeightAboveFloorMm: bottomEdgeHeightAboveFloorMm ?? 2700,
      floor: mcbimSpace.buildingStory?.name ?? "",
      projectNo: String(projectNo),
      roomName: room.roomName ?? mcbimSpace.longName,
      roomNumber: room.roomNumber,
      tileSize,
    };

    const snapToGrid = selectSnapToGrid(state);
    const ceilingGrids = mcbimSpace?.ceilingGrids;
    if (snapToGrid && ceilingGrids != null && ceilingGrids.length > 0) {
      try {
        const adjusted = adjustObject(
          {
            center: [device.coordinate[0], device.coordinate[1]],
            direction: device.direction,
            tileSize,
          },
          ceilingGrids
        );
        if (adjusted) {
          device.coordinate = withWorldZInCoordinate(
            adjusted.center,
            floorElevationM,
            bottomEdgeHeightAboveFloorMm ?? 2700
          );
          if (adjusted.direction) device.direction = adjusted.direction;
        }
      } catch (e) {
        console.warn("Error adjusting device position on creation", e);
      }
    }

    await dispatch(
      openapi.endpoints.postMultiplacerDeviceAddDevices.initiate({
        body: { devices: [device] },
      })
    );
  };
};

export type IGenericDeviceInput = {
  deviceCategory: IDevice["deviceCategory"];
  tileSize: [number, number];
  description?: string;
};

export const addGenericDeviceToSelectedRoomThunk = (input: IGenericDeviceInput): IAppThunk => {
  return async (dispatch, getState) => {
    const state = getState();
    const selectedSpaceNumber = state.design.selectedSpaceNumber;
    const projectNo = selectProjectNo(state);

    if (!selectedSpaceNumber) {
      console.log("No selected space number");
      return;
    }

    const room = selectHttpSingleRoomFull(state);
    if (!room) {
      console.log("No active room found");
      return;
    }

    const mcbimSpace = selectActivePartialMcbimSpace(state);
    if (!mcbimSpace?.polygon) {
      console.log("No active McBim space found");
      return;
    }

    const spaces = selectHttpMcbimSpacesResult(state);
    const spaceForRoom = spaces.find((s: any) => s?.name === room.roomNumber);
    const bottomEdgeHeightAboveFloorMm =
      getHeightFromFirstCeilingGrid(spaceForRoom?.ceilingGrids ?? []) ?? 2700;
    const floorElevationM = state.design.floorAndJob?.elevation;

    const firstLine = mcbimSpace.polygon[0];
    const secondLine = mcbimSpace.polygon[1];
    const pointAPointB: IPointAPointB = {
      pointA: { x: firstLine[0], y: firstLine[1] },
      pointB: { x: secondLine[0], y: secondLine[1] },
    };
    const direction = convertAVectorTo60cm(getPointASubPointB(pointAPointB));
    const coordinate = getRandomCoordinateInPolygon(mcbimSpace.polygon as number[][]);
    const coordinate2d = [coordinate.x, coordinate.y];

    const device: IDevice = {
      description: input.description?.trim() || `Generisk komponent ${input.tileSize.join("x")}`,
      deviceCategory: input.deviceCategory,
      discipline: DEVICE_DISCIPLINE_MAPPING[input.deviceCategory],
      compCode: "",
      coordinate: withWorldZInCoordinate(
        coordinate2d,
        floorElevationM,
        bottomEdgeHeightAboveFloorMm
      ),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      direction,
      bottomEdgeHeightAboveFloorMm,
      floor: mcbimSpace.buildingStory?.name ?? "",
      projectNo: String(projectNo),
      roomName: room.roomName ?? mcbimSpace.longName,
      roomNumber: room.roomNumber,
      tileSize: input.tileSize,
    };

    const snapToGrid = selectSnapToGrid(state);
    const ceilingGrids = mcbimSpace.ceilingGrids;
    if (snapToGrid && ceilingGrids != null && ceilingGrids.length > 0) {
      try {
        const adjusted = adjustObject(
          {
            center: [device.coordinate[0], device.coordinate[1]],
            direction: device.direction,
            tileSize: input.tileSize,
          },
          ceilingGrids
        );
        if (adjusted) {
          device.coordinate = withWorldZInCoordinate(
            adjusted.center,
            floorElevationM,
            bottomEdgeHeightAboveFloorMm
          );
          if (adjusted.direction) device.direction = adjusted.direction;
        }
      } catch (e) {
        console.warn("Error adjusting generic device position on creation", e);
      }
    }

    await dispatch(
      openapi.endpoints.postMultiplacerDeviceAddDevices.initiate({
        body: { devices: [device] },
      })
    );
  };
};
